import ApprovalPopup from 'organisms/ApprovalPopup';
import ApprovalWaiting from 'organisms/ApprovalWaiting';
import { useEffect, useRef, useState } from 'react';
import { socket, socketEmit } from 'services/socket';
import moment from 'moment';
import { BOND_TYPE_ENUM, formatSpreadDiffStartLabel } from 'lib/constants';

export default function TradeRequest() {
    const [showApprovalWaiting, setShowApprovalWaiting] = useState(false);
    const [awaitingTradeRequests, setAwaitingTradeRequests] = useState([]);
    const [showApprovalPopup, setShowApprovalPopup] = useState(false);
    const [receivedTradeRequests, setReceivedTradeRequests] = useState([]);
    const [currentRequest, setCurrentRequest] = useState(null);
    const [isInsufficientSize, setIsInsufficientSize] = useState(false);
    const receivedTradeRequestsRef = useRef(receivedTradeRequests);
    const showApprovalPopupRef = useRef(showApprovalPopup);
    const currentRequestRef = useRef(null);
    const tradeRequestActionQueue = useRef([]);
    const isProcessingAction = useRef(false);
    const tradeRequestActionHandler = useRef(null);

    const onTradeRequestCreation = () => {
        socketEmit('/get/trade-requests');
    };

    const isMeetingDates = (bond) => bond.type === 'meetingDates';

    const formatTradeRequestDetails = (data) => data?.map((item) => {
        if (item.BondPrice.IrsBond) {
            const isMeeting = isMeetingDates(item.BondPrice.IrsBond);
            const irsBond = item.BondPrice.IrsBond;
            const isDiffStart = [
                BOND_TYPE_ENUM.SPREAD_DIFF_START_DATE,
                BOND_TYPE_ENUM.BOB_SPREAD_DIFF_START_DATE,
                BOND_TYPE_ENUM.SIXS_SPREAD_DIFF_START_DATE,
                BOND_TYPE_ENUM.BUTTERFLY_DIFF_START_DATE,
                BOND_TYPE_ENUM.BOB_BUTTERFLY_DIFF_START_DATE,
                BOND_TYPE_ENUM.SIXS_BUTTERFLY_DIFF_START_DATE
            ].includes(irsBond.type);
            return ({
                bondName: `${irsBond.irs.name}${isMeeting ? ' - Meeting Dates' : isDiffStart ? ' - Bespoke' : irsBond.end2
                    ? ' - Butterfly' : irsBond.end1 ? ' - Spread' : irsBond.startLabel === 'Spot' ? '' : ' FWD START/FRAs'}`,
                details: [
                    isMeeting ? { label: 'Meeting Date', value: `${irsBond.startLabel}${irsBond.start1Label ? ` -> ${irsBond.start1Label}` : ''}` } : null,
                    isDiffStart ? {
                        label: 'Custom',
                        value: formatSpreadDiffStartLabel(irsBond.startLabel, irsBond.endLabel, irsBond.start1Label, irsBond.end1Label, irsBond.start2Label, irsBond.end2Label)
                    } : null,
                    !isDiffStart ? {
                        label: `Start Date${irsBond.start1 ? ' (Leg1)' : ''}`,
                        value: `${isMeeting ? moment(irsBond.start, 'YYYY-MM-DD').format('DD MMM YYYY') : irsBond.startLabel}${irsBond.immLabel ? ` -> ${irsBond.immLabel}
                             | ${moment(irsBond.start).format('DD/MM/YYYY')} -> ${moment(irsBond.imm).format('DD/MM/YYYY')}` : ''}`
                    } : null,
                    ...(isMeeting ? [
                        { label: `End Date${irsBond.start1 ? ' (Leg1)' : ''}`, value: `${moment(irsBond.end, 'YYYY-MM-DD').format('DD MMM YYYY')}` },
                        ...(irsBond.start1 ? [
                            { label: 'Start Date (Leg2)', value: moment(irsBond.start1, 'YYYY-MM-DD').format('DD MMM YYYY') },
                            { label: 'End Date (Leg2)', value: moment(irsBond.end1, 'YYYY-MM-DD').format('DD MMM YYYY') }
                        ] : [])
                    ] : []),
                    !isMeeting && !isDiffStart ? {
                        label: 'Tenor',
                        value: irsBond.end1
                            ? `${`${irsBond.end1Label.toLowerCase().replace(/ /g, '')} x `}${irsBond.end2Label
                                ? `${irsBond.end2Label.toLowerCase().replace(/ /g, '')} x ` : ''}${irsBond.endLabel.toLowerCase().replace(/ /g, '')}`
                            : irsBond.endLabel
                    } : null,
                    {
                        label: `${irsBond.end1 && !isMeeting ? 'Trade Level' : 'Rate'}`,
                        value: item.BondPrice.price,
                        ...(irsBond.end1 && !isMeeting && { unit: 'bps' })
                    },
                    { label: 'DV01', value: item.size, unit: 'k' },
                    ...(irsBond.end2 ? [
                        { label: 'Notional (Leg1)', value: item.notional1, unit: `${irsBond.irs.currency}(M)` },
                        { label: 'Notional (Leg2)', value: item.notional2, unit: `${irsBond.irs.currency}(M)` },
                        { label: 'Notional (Leg3)', value: item.notional, unit: `${irsBond.irs.currency}(M)` }
                    ] : irsBond.end1 && !isMeeting ? [
                        { label: 'Notional (Leg1)', value: item.notional1, unit: `${irsBond.irs.currency}(M)` },
                        { label: 'Notional (Leg2)', value: item.notional, unit: `${irsBond.irs.currency}(M)` }
                    ] : [
                        { label: 'Notional', value: item.notional, unit: `${irsBond.irs.currency}(M)` }
                    ])
                ].filter(Boolean),
                expiry: item.requestExpiry,
                tradeRequestId: item.id,
                size: `${Number.isNaN(Number(item.size)) ? '' : Number(item.size).toLocaleString('en-US')}k DV01`,
                tradeSize: item.size,
                requestType: item.BondPrice.type,
                requestorId: item.requestorId,
                bondType: 'Irs',
                bondPriceId: item.bondPriceId,
                isTraderPresent: !!item.BondPrice.traderId,
                brokerType: item.BondPrice.brokerType
            });
        }
        return {
            bondName: `${item.BondPrice.CurrencyBond.currency.name}${item.BondPrice.CurrencyBond.end1 ? ' - Liquidity Swaps' : ''}`,
            details: [
                { label: 'Start Date', value: item.BondPrice.CurrencyBond.startLabel },
                {
                    label: 'Tenor',
                    value: item.BondPrice.CurrencyBond.end1
                        ? `${`${item.BondPrice.CurrencyBond.end1Label.toLowerCase().replace(/ /g, '')} x `}${item.BondPrice.CurrencyBond.end2Label
                            ? `${item.BondPrice.CurrencyBond.end2Label.toLowerCase()
                                .replace(/ /g, '')} x ` : ''}${item.BondPrice.CurrencyBond.endLabel.toLowerCase().replace(/ /g, '')}`
                        : item.BondPrice.CurrencyBond.endLabel
                },
                {
                    label: 'Exchange rate',
                    value: item.BondPrice.price,
                    unit: item.BondPrice.CurrencyBond.end1 ? 'swp pts' : item.BondPrice.CurrencyBond.currency.currency
                },
                { label: 'Notional', value: item.size, unit: 'USD(M)' }
            ],
            size: `${Number.isNaN(Number(item.size)) ? '' : Number(item.size).toLocaleString('en-US')}M Notional`,
            expiry: item.requestExpiry,
            tradeRequestId: item.id,
            tradeSize: item.size,
            requestType: item.BondPrice.type,
            requestorId: item.requestorId,
            bondType: 'Currency',
            bondPriceId: item.bondPriceId,
            isTraderPresent: !!item.BondPrice.traderId,
            brokerType: item.BondPrice.brokerType
        };
    });

    const onTradeRequestsFetch = ({ data }) => {
        const requestDetails = formatTradeRequestDetails(data);
        setAwaitingTradeRequests(requestDetails);
        setShowApprovalWaiting(true);
    };

    const showNextRequest = (bondPriceId = null) => {
        setReceivedTradeRequests((prevRequests) => {
            const filteredRequests = prevRequests?.filter(
                (request) => request?.bondPriceId !== bondPriceId
            );

            if (filteredRequests.length > 0) {
                const [nextRequest, ...remainingRequests] = filteredRequests;
                setCurrentRequest(nextRequest);
                nextRequest.action === 'InsufficientSize' ? setIsInsufficientSize(true) : setIsInsufficientSize(false);
                setShowApprovalPopup(true);
                return remainingRequests;
            }

            return filteredRequests;
        });
    };

    const onTradeRequestReceived = (data) => {
        const isPopupVisible = showApprovalPopupRef.current;
        setReceivedTradeRequests((prevRequests) => [...prevRequests, { ...data.details, action: null }]);
        if (!isPopupVisible) {
            showNextRequest(null);
        }
    };

    const onOwnerTradeRequestsFetch = ({ data }) => {
        if (data?.length) {
            const requestDetails = formatTradeRequestDetails(data);
            setReceivedTradeRequests(requestDetails);
            showNextRequest(null);
        }
    };

    const resetTimer = (expiry) => {
        const expiryTime = new Date(expiry);
        const currentTime = new Date();
        return Math.max(Math.floor((expiryTime - currentTime) / 1000), 0);
    };

    const closeApprovalWaitingPopup = () => {
        setShowApprovalWaiting(false);
    };

    const handleClosePopup = (bondPriceId) => {
        setShowApprovalPopup(false);
        showNextRequest(bondPriceId);
    };

    const closeApprovalPopup = (bondPriceId = null) => {
        const channel = new BroadcastChannel('trade-popup');
        channel.postMessage({ type: 'CLOSE_APPROVAL_POPUP', bondPriceId });
        channel.close();
    };

    const handleTradeRequestAction = (data) => {
        if (data.action === 'CloseTradeRequest') {
            const tradeRequestId = data?.tradeRequestId;
            const bondPriceId = data?.closePendingRequestsOfBondPrice ? data?.bondPriceId : null;

            if (currentRequestRef.current?.tradeRequestId === tradeRequestId) {
                if (!['Accept', 'Decline'].includes(currentRequestRef.current?.action)) {
                    closeApprovalPopup(bondPriceId);
                }
            } else {
                setReceivedTradeRequests((prevRequests) => prevRequests.filter((request) => request.tradeRequestId !== tradeRequestId));
            }
        } else {
            const isPopupVisible = showApprovalPopupRef.current;
            setReceivedTradeRequests((prevRequests) => [...prevRequests, { ...data.details, action: data.action }]);
            if (!isPopupVisible) {
                showNextRequest(null);
            }
            setAwaitingTradeRequests((prevRequests) => {
                const updatedRequests = prevRequests.filter(
                    (request) => request.tradeRequestId !== data.details.tradeRequestId
                );
                if (updatedRequests.length === 0) {
                    closeApprovalWaitingPopup();
                }
                return updatedRequests;
            });
        }
    };

    const processNextAction = () => {
        if (isProcessingAction.current || tradeRequestActionQueue.current.length === 0) return;

        isProcessingAction.current = true;

        const nextAction = tradeRequestActionQueue.current.shift();
        if (nextAction) {
            handleTradeRequestAction(nextAction);
        }

        setTimeout(() => {
            isProcessingAction.current = false;
            processNextAction();
        }, 50);
    };

    useEffect(() => {
        const channel = new BroadcastChannel('trade-popup');
        channel.onmessage = (e) => {
            if (e.data?.type === 'CLOSE_APPROVAL_POPUP') {
                handleClosePopup(e.data.bondPriceId);
            }
        };
        return () => channel.close();
    }, []);

    useEffect(() => {
        receivedTradeRequestsRef.current = receivedTradeRequests;
    }, [receivedTradeRequests]);

    useEffect(() => {
        showApprovalPopupRef.current = showApprovalPopup;
    }, [showApprovalPopup]);

    useEffect(() => {
        currentRequestRef.current = currentRequest;
    }, [currentRequest]);

    useEffect(() => {
        tradeRequestActionHandler.current = (data) => {
            tradeRequestActionQueue.current.push(data);
            processNextAction();
        };

        socket.on('/post/trade-requests', onTradeRequestCreation);
        socket.on('/get/trade-requests', onTradeRequestsFetch);
        socket.on('/trade-request-action', tradeRequestActionHandler.current);
        socket.on('/trade-request', onTradeRequestReceived);
        socket.on('/get/trade-requests-by-owner', onOwnerTradeRequestsFetch);

        socketEmit('/get/trade-requests');
        socketEmit('/get/trade-requests-by-owner');

        return () => {
            socket.off('/post/trade-requests', onTradeRequestCreation);
            socket.off('/get/trade-requests', onTradeRequestsFetch);
            socket.off('/trade-request-action', tradeRequestActionHandler.current);
            socket.off('/trade-request', onTradeRequestReceived);
            socket.on('/get/trade-requests-by-owner', onOwnerTradeRequestsFetch);
        };
    }, []);

    return <>
        {showApprovalWaiting && <ApprovalWaiting
            infoTable={awaitingTradeRequests}
            setAwaitingTradeRequests={setAwaitingTradeRequests}
            closePopup={closeApprovalWaitingPopup}
        />}

        {showApprovalPopup && currentRequest && (
            <ApprovalPopup
                infoTable={currentRequest}
                closePopup={closeApprovalPopup}
                tradeRequestAction={currentRequest?.action || null}
                resetTimer={() => resetTimer(currentRequest.expiry)}
                isInsufficientSize={isInsufficientSize}
            />
        )}
    </>;
}
